// Compact classic Metaphone (Lawrence Philips, 1990) + a small phonetic-equality helper, so
// spoken-answer detection survives ASR homophones/mis-spellings ("no i deer" ~ "no idea",
// "flower" ~ "flour", "nite" ~ "night") WITHOUT hand-authoring variant lists per puzzle.
// No dependencies. Used by answerMatch.ts.

const VOWELS = 'AEIOU'
const isV = (c: string) => VOWELS.includes(c)

export function metaphone(input: string): string {
  let w = String(input).toUpperCase().replace(/[^A-Z]/g, '')
  if (!w) return ''
  // initial-letter transforms
  if (/^(AE|GN|KN|PN|WR)/.test(w)) w = w.slice(1)
  else if (w[0] === 'X') w = 'S' + w.slice(1)
  else if (w.startsWith('WH')) w = 'W' + w.slice(2)

  const n = w.length
  const at = (i: number) => (i >= 0 && i < n ? w[i] : '')
  const vowelAt = (i: number) => isV(at(i))
  let out = ''

  for (let i = 0; i < n; i++) {
    const c = w[i]
    const prev = at(i - 1)
    const next = at(i + 1)
    const next2 = at(i + 2)
    if (c === prev && c !== 'C') continue // drop doubles (except C)

    switch (c) {
      case 'A': case 'E': case 'I': case 'O': case 'U':
        if (i === 0) out += c
        break
      case 'B':
        if (!(i === n - 1 && prev === 'M')) out += 'B' // silent B in "-MB"
        break
      case 'C':
        if (next === 'I' && next2 === 'A') out += 'X'
        else if (next === 'H') { out += prev === 'S' ? 'K' : 'X'; i++ }
        else if ('IEY'.includes(next)) { if (prev !== 'S') out += 'S' }
        else out += 'K'
        break
      case 'D':
        if (next === 'G' && 'IEY'.includes(next2)) { out += 'J'; i += 2 } else out += 'T'
        break
      case 'F': out += 'F'; break
      case 'G':
        if (next === 'H') { if (!(i > 0 && vowelAt(i - 1))) { /* silent */ } i++ }
        else if (next === 'N') { /* silent GN */ }
        else if ('IEY'.includes(next)) out += 'J'
        else out += 'K'
        break
      case 'H':
        if (vowelAt(i - 1) && !vowelAt(i + 1)) { /* silent after vowel, before consonant */ }
        else if ('CSPTG'.includes(prev)) { /* handled by preceding letter */ }
        else out += 'H'
        break
      case 'J': out += 'J'; break
      case 'K': if (prev !== 'C') out += 'K'; break
      case 'L': out += 'L'; break
      case 'M': out += 'M'; break
      case 'N': out += 'N'; break
      case 'P': if (next === 'H') { out += 'F'; i++ } else out += 'P'; break
      case 'Q': out += 'K'; break
      case 'R': out += 'R'; break
      case 'S':
        if (next === 'H') { out += 'X'; i++ }
        else if (next === 'I' && 'OA'.includes(next2)) out += 'X'
        else out += 'S'
        break
      case 'T':
        if (next === 'H') { out += '0'; i++ } // theta
        else if (next === 'I' && 'OA'.includes(next2)) out += 'X'
        else out += 'T'
        break
      case 'V': out += 'F'; break
      case 'W': case 'Y': if (vowelAt(i + 1)) out += c; break
      case 'X': out += 'KS'; break
      case 'Z': out += 'S'; break
    }
  }
  return out
}

// Levenshtein (small strings; phonetic codes are short).
function lev(a: string, b: string): number {
  const m = a.length, n = b.length
  if (!m) return n; if (!n) return m
  let prev = Array.from({ length: n + 1 }, (_, j) => j)
  for (let i = 1; i <= m; i++) {
    const cur = [i]
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1))
    }
    prev = cur
  }
  return prev[n]
}

// Do two strings sound alike? Equal metaphone, or within 1 edit for codes of length >= 3 (absorbs a
// trailing sound the ASR added/dropped, e.g. "idea"->IT vs "ideer"->ITR). Short codes must match exactly.
export function soundsLike(a: string, b: string): boolean {
  const ca = metaphone(a), cb = metaphone(b)
  if (!ca || !cb) return false
  if (ca === cb) return true
  return Math.max(ca.length, cb.length) >= 3 && lev(ca, cb) <= 1
}
